import React, { useState } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";

interface BookingDebugInfo {
  id: number;
  orderNumber: string;
  customer: string;
  chaletId: string;
  chaletType: string;
  arrival: string;
  departure: string;
  nights: number;
  status: string;
  isBlocked: boolean;
  WARNING?: string;
}

interface ChaletTypeDebug {
  total: number;
  assigned: number;
  nullBookings: number;
  calculated_available: number;
  bookings: BookingDebugInfo[];
}

interface DebugResponse {
  dateRange?: {
    start?: string;
    end?: string;
    nights?: number;
  };
  summary?: {
    totalBookings?: number;
    bigChaletBookings?: number;
    smallChaletBookings?: number;
    nullChaletBookings?: number;
    WARNING?: string;
  };
  bigChalets?: ChaletTypeDebug;
  smallChalets?: ChaletTypeDebug;
  nullChaletBookings?: BookingDebugInfo[];
}

const DebugAvailability: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [data, setData] = useState<DebugResponse | null>(null);
  const [rawJson, setRawJson] = useState<string>("");
  const [showRawJson, setShowRawJson] = useState<boolean>(false);
  
  // NEW: Also fetch from calendar-availability to compare
  const [calendarData, setCalendarData] = useState<any>(null);
  const [calendarRawJson, setCalendarRawJson] = useState<string>("");
  const [showCalendarJson, setShowCalendarJson] = useState<boolean>(false);
  
  // NEW: Per-day breakdown (what the calendar actually queries)
  const [perDayData, setPerDayData] = useState<Array<{date: string, bigAvailable: number, bigTotal: number, smallAvailable: number, smallTotal: number}>>([]);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Safe data extraction helpers
  const summary = data?.summary || {};
  const dateRange = data?.dateRange || {};
  const bigChalets = data?.bigChalets || { total: 5, assigned: 0, nullBookings: 0, calculated_available: 5, bookings: [] };
  const smallChalets = data?.smallChalets || { total: 5, assigned: 0, nullBookings: 0, calculated_available: 5, bookings: [] };
  const nullChaletBookings = data?.nullChaletBookings || [];

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const fetchDebugData = async () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates.");
      return;
    }

    setLoading(true);
    setError("");
    setData(null);
    setRawJson("");
    setCalendarData(null);
    setCalendarRawJson("");
    setPerDayData([]);

    try {
      const formattedStart = format(startDate, "yyyy-MM-dd");
      const formattedEnd = format(endDate, "yyyy-MM-dd");

      // Fetch BOTH endpoints in parallel for comparison
      const [debugResponse, calendarResponse] = await Promise.all([
        // Debug endpoint (no auth)
        axios.get<DebugResponse>(
          `${apiUrl}/PackageBooking/debug-availability`,
          {
            params: {
              startDate: formattedStart,
              endDate: formattedEnd,
            },
          }
        ),
        // Calendar availability endpoint (with auth) - THIS IS WHAT THE CALENDAR USES
        axios.get(
          `${apiUrl}/PackageBooking/calendar-availability`,
          {
            params: {
              startDate: formattedStart,
              endDate: formattedEnd,
            },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        ).catch(err => {
          console.error("calendar-availability error:", err);
          return { data: { error: err.message, response: err.response?.data } };
        }),
      ]);

      setData(debugResponse.data);
      setRawJson(JSON.stringify(debugResponse.data, null, 2));
      
      setCalendarData(calendarResponse.data);
      setCalendarRawJson(JSON.stringify(calendarResponse.data, null, 2));
      
      console.log("DEBUG endpoint response:", debugResponse.data);
      console.log("CALENDAR endpoint response:", calendarResponse.data);
      
      // NEW: Parse per-day data from the calendar response's dailyAvailability array
      // (single call already done above — no need for N separate requests)
      const dailyResults: Array<{date: string, bigAvailable: number, bigTotal: number, smallAvailable: number, smallTotal: number}> = [];
      const calData: any = calendarResponse.data;

      if (calData?.dailyAvailability && Array.isArray(calData.dailyAvailability)) {
        for (const dayEntry of calData.dailyAvailability) {
          const dateKey = String(dayEntry.date).split("T")[0];
          let bigAvail = 0, bigTotal = 0, smallAvail = 0, smallTotal = 0;

          if (Array.isArray(dayEntry.byType)) {
            for (const t of dayEntry.byType) {
              const ct = String(t.chaletType).toLowerCase().trim();
              const avail = typeof t.available === "number" ? t.available : 0;
              const total = typeof t.total === "number" ? t.total : 0;

              if (ct === "big" || ct === "0" || t.chaletType === 0) {
                bigAvail = avail;
                bigTotal = total;
              } else if (ct === "small" || ct === "1" || t.chaletType === 1) {
                smallAvail = avail;
                smallTotal = total;
              }
            }
          }

          dailyResults.push({ date: dateKey, bigAvailable: bigAvail, bigTotal, smallAvailable: smallAvail, smallTotal });
        }
      } else if (calData?.availabilityByType && Array.isArray(calData.availabilityByType)) {
        // Fallback: old aggregate format — apply same numbers to every day
        let bigAvail = 0, bigTotal = 0, smallAvail = 0, smallTotal = 0;
        for (const item of calData.availabilityByType) {
          const ct = String(item.chaletType).toLowerCase().trim();
          const avail = typeof item.availableChalets === "number" ? item.availableChalets : (typeof item.available === "number" ? item.available : 0);
          const total = typeof item.totalChalets === "number" ? item.totalChalets : (typeof item.total === "number" ? item.total : 0);

          if (ct === "big" || ct === "0" || item.chaletType === 0) { bigAvail = avail; bigTotal = total; }
          else if (ct === "small" || ct === "1" || item.chaletType === 1) { smallAvail = avail; smallTotal = total; }
        }

        const currentDate = new Date(startDate);
        const endDateObj = new Date(endDate);
        while (currentDate < endDateObj) {
          dailyResults.push({ date: format(currentDate, "yyyy-MM-dd"), bigAvailable: bigAvail, bigTotal, smallAvailable: smallAvail, smallTotal });
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
      
      setPerDayData(dailyResults);
      console.log("Per-day availability:", dailyResults);
    } catch (err: any) {
      console.error("Error fetching debug data:", err);
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setError(`${err.response.data.error}: ${err.response.data.message || ''}`);
      } else {
        setError("Failed to fetch debug data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(rawJson);
    alert("JSON copied to clipboard!");
  };
  
  const copyCalendarToClipboard = () => {
    navigator.clipboard.writeText(calendarRawJson);
    alert("Calendar JSON copied to clipboard!");
  };

  return (
    <div className="container mt-4 mb-5">
      <div className="card border-danger">
        <div className="card-header bg-danger text-white">
          <h3 className="mb-0">
            <i className="fas fa-bug me-2"></i>
            🔧 DEBUG: Raw Availability Calculator
          </h3>
          <small>Emergency debugging tool - shows exactly what the backend sees</small>
        </div>

        <div className="card-body">
          {/* Date Range Selector */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label fw-bold">Select Date Range to Debug:</label>
              <DatePicker
                selected={startDate}
                onChange={handleDateChange}
                startDate={startDate ?? undefined}
                endDate={endDate ?? undefined}
                selectsRange
                className="form-control"
                placeholderText="Click to select dates"
                dateFormat="yyyy-MM-dd"
                calendarStartDay={1}
              />
            </div>
            <div className="col-md-6 d-flex align-items-end">
              <button
                className="btn btn-danger"
                onClick={fetchDebugData}
                disabled={loading || !startDate || !endDate}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Run Debug Query
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Results */}
          {data && (
            <>
              {/* CRITICAL WARNING for NULL chalets */}
              {(summary.nullChaletBookings || 0) > 0 && (
                <div className="alert alert-danger mb-4" role="alert">
                  <h4 className="alert-heading">
                    <i className="fas fa-radiation me-2"></i>
                    🚨 CRITICAL: {summary.nullChaletBookings || 0} BOOKING(S) WITHOUT CHALET ASSIGNMENT!
                  </h4>
                  <p className="mb-2">
                    These bookings are <strong>blocking availability</strong> but have no chalet assigned.
                    This is the ROOT CAUSE of the availability calculation problems!
                  </p>
                  <hr />
                  <p className="mb-0">
                    <strong>FIX REQUIRED:</strong> Each booking below needs a ChaletId assigned in the database.
                  </p>
                </div>
              )}

              {/* CRITICAL: Compare calendar-availability vs debug-availability */}
              {calendarData && (
                <div className="card mb-4 border-warning">
                  <div className="card-header bg-warning text-dark">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    🔍 CALENDAR ENDPOINT COMPARISON (What the calendar actually sees)
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-3">
                      This shows what <code>/PackageBooking/calendar-availability</code> returns - 
                      the calendar uses this data to show "Ledig stor stuga (X)"
                    </p>
                    
                    {calendarData.availabilityByType ? (
                      <div className="row">
                        {calendarData.availabilityByType.map((typeData: any, idx: number) => {
                          const isBig = String(typeData.chaletType).toLowerCase() === 'big' || typeData.chaletType === 0;
                          const total = typeData.totalChalets ?? typeData.total ?? '?';
                          const booked = typeData.bookedChalets ?? typeData.booked ?? '?';
                          const available = typeData.availableChalets ?? typeData.available ?? '?';
                          return (
                            <div key={idx} className="col-md-6">
                              <div className={`card ${isBig ? 'border-primary' : 'border-info'} mb-3`}>
                                <div className={`card-header ${isBig ? 'bg-primary' : 'bg-info'} text-white`}>
                                  {isBig ? '🏠 BIG Chalets' : '🏡 SMALL Chalets'} (Calendar View)
                                </div>
                                <div className="card-body">
                                  <div className="row text-center">
                                    <div className="col-4">
                                      <h4>{total}</h4>
                                      <small>Total</small>
                                    </div>
                                    <div className="col-4">
                                      <h4 className="text-danger">{booked}</h4>
                                      <small>Booked</small>
                                    </div>
                                    <div className="col-4">
                                      <h4 className={Number(available) > 0 ? "text-success" : "text-danger"}>
                                        {available}
                                      </h4>
                                      <small>Available</small>
                                    </div>
                                  </div>
                                  {typeData.availableChaletIds && (
                                    <div className="mt-2">
                                      <small>Available IDs: {typeData.availableChaletIds.length > 0 ? typeData.availableChaletIds.join(', ') : 'None'}</small>
                                    </div>
                                  )}
                                  {typeData.bookedChaletIds && (
                                    <div className="mt-1">
                                      <small>Booked IDs: {typeData.bookedChaletIds.length > 0 ? typeData.bookedChaletIds.join(', ') : 'None'}</small>
                                    </div>
                                  )}
                                  {typeData.availabilityNote && (
                                    <div className="alert alert-warning mt-3 mb-0" style={{ fontSize: '13px' }}>
                                      <i className="fas fa-info-circle me-1"></i>
                                      <strong>Why 0 available even though some days show capacity:</strong>{' '}
                                      {typeData.availabilityNote}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="alert alert-danger">
                        <strong>ERROR:</strong> Calendar endpoint did not return expected format!
                        <pre className="mt-2 bg-dark text-light p-2 rounded" style={{ fontSize: '12px' }}>
                          {JSON.stringify(calendarData, null, 2)}
                        </pre>
                      </div>
                    )}
                    
                    {/* Show raw calendar JSON */}
                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => setShowCalendarJson(!showCalendarJson)}
                      >
                        {showCalendarJson ? "Hide" : "Show"} Raw Calendar JSON
                      </button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={copyCalendarToClipboard}>
                        Copy
                      </button>
                    </div>
                    {showCalendarJson && (
                      <pre className="bg-dark text-light p-3 rounded mt-2" style={{ maxHeight: "300px", overflow: "auto", fontSize: '11px' }}>
                        {calendarRawJson}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* Per-Day Breakdown - EXACTLY what the calendar queries */}
              {perDayData.length > 0 && (
                <div className="card mb-4 border-info">
                  <div className="card-header bg-info text-white">
                    <i className="fas fa-calendar-day me-2"></i>
                    📅 PER-DAY BREAKDOWN (From dailyAvailability)
                  </div>
                  <div className="card-body">
                    <p className="text-muted small mb-3">
                      This shows <strong>per-day availability</strong> from the calendar endpoint.
                      Same-day turnovers are accounted for (departure day frees the chalet for new arrivals).
                    </p>
                    <table className="table table-sm table-bordered">
                      <thead className="table-dark">
                        <tr>
                          <th>Date</th>
                          <th className="text-center">Big Chalets</th>
                          <th className="text-center">Small Chalets</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {perDayData.map((day) => {
                          const bigFull = day.bigAvailable === 0 && day.bigTotal > 0;
                          const smallFull = day.smallAvailable === 0 && day.smallTotal > 0;
                          const allFull = bigFull && smallFull;
                          const hasNull = (data?.nullChaletBookings?.length || 0) > 0;
                          return (
                            <tr key={day.date} className={allFull ? '' : ''}>
                              <td><strong>{day.date}</strong></td>
                              <td className={`text-center ${bigFull ? 'bg-danger bg-opacity-25' : 'bg-success bg-opacity-25'}`}>
                                <strong>{day.bigAvailable}</strong> / {day.bigTotal} available
                              </td>
                              <td className={`text-center ${smallFull ? 'bg-danger bg-opacity-25' : 'bg-success bg-opacity-25'}`}>
                                <strong>{day.smallAvailable}</strong> / {day.smallTotal} available
                              </td>
                              <td className="text-center">
                                {allFull ? (
                                  <span className="badge bg-danger">Fully Booked</span>
                                ) : hasNull ? (
                                  <span className="badge bg-warning text-dark">⚠️ Has NULL bookings</span>
                                ) : (
                                  <span className="badge bg-success">
                                    {day.bigAvailable + day.smallAvailable} available
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="alert alert-info mt-2 mb-0">
                      <strong>Legend:</strong> 
                      <span className="badge bg-success ms-2">Green = Has availability</span>
                      <span className="badge bg-danger ms-2">Red = Fully booked (0 available)</span>
                      <span className="badge bg-warning text-dark ms-2">Yellow = NULL chalet bookings exist</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Summary Card */}
              <div className="card mb-4 border-dark">
                <div className="card-header bg-dark text-white">
                  <i className="fas fa-calculator me-2"></i>
                  Summary for {dateRange.start || 'N/A'} to {dateRange.end || 'N/A'} ({dateRange.nights || 0} nights)
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3 text-center">
                      <h4>{summary.totalBookings || 0}</h4>
                      <small>Total Overlapping Bookings</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h4 className="text-primary">{summary.bigChaletBookings || 0}</h4>
                      <small>Big Chalet Bookings</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h4 className="text-info">{summary.smallChaletBookings || 0}</h4>
                      <small>Small Chalet Bookings</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h4 className={(summary.nullChaletBookings || 0) > 0 ? "text-danger" : "text-success"}>
                        {summary.nullChaletBookings || 0}
                      </h4>
                      <small>⚠️ NULL Chalet Bookings</small>
                    </div>
                  </div>
                  <div className="mt-3 text-center">
                    <span className={`badge ${(summary.WARNING || '').includes("NULL") ? "bg-danger" : "bg-success"} fs-6`}>
                      {summary.WARNING || 'No warnings'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Big Chalets Breakdown */}
              <div className="card mb-4 border-primary">
                <div className="card-header bg-primary text-white">
                  <i className="fas fa-home me-2"></i>
                  BIG Chalets (4-bed) - Total: {bigChalets.total}
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3 text-center">
                      <h5 className="text-danger">{bigChalets.assigned}</h5>
                      <small>Distinct Chalets Booked</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5 className={bigChalets.nullBookings > 0 ? "text-danger" : "text-muted"}>
                        {bigChalets.nullBookings}
                      </h5>
                      <small>NULL Chalet Bookings</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5 className="text-success">{bigChalets.calculated_available}</h5>
                      <small>Calculated Available</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5>{bigChalets.bookings.length}</h5>
                      <small>Total Booking Records</small>
                    </div>
                  </div>

                  {bigChalets.bookings.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th>ID</th>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Chalet ID</th>
                            <th>Arrival</th>
                            <th>Departure</th>
                            <th>Nights</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bigChalets.bookings.map((b) => (
                            <tr key={b.id} className={(b.chaletId || '').includes("NULL") ? "table-danger" : ""}>
                              <td>{b.id}</td>
                              <td><code>{b.orderNumber}</code></td>
                              <td>{b.customer}</td>
                              <td>
                                {(b.chaletId || '').includes("NULL") ? (
                                  <span className="badge bg-danger">⚠️ {b.chaletId}</span>
                                ) : (
                                  <span className="badge bg-secondary">#{b.chaletId}</span>
                                )}
                              </td>
                              <td>{b.arrival}</td>
                              <td>{b.departure}</td>
                              <td>{b.nights}</td>
                              <td>
                                <span className={`badge ${b.isBlocked ? "bg-dark" : "bg-success"}`}>
                                  {b.isBlocked ? "Blocked" : b.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Small Chalets Breakdown */}
              <div className="card mb-4 border-info">
                <div className="card-header bg-info text-white">
                  <i className="fas fa-home me-2"></i>
                  SMALL Chalets (2-bed) - Total: {smallChalets.total}
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3 text-center">
                      <h5 className="text-danger">{smallChalets.assigned}</h5>
                      <small>Distinct Chalets Booked</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5 className={smallChalets.nullBookings > 0 ? "text-danger" : "text-muted"}>
                        {smallChalets.nullBookings}
                      </h5>
                      <small>NULL Chalet Bookings</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5 className="text-success">{smallChalets.calculated_available}</h5>
                      <small>Calculated Available</small>
                    </div>
                    <div className="col-md-3 text-center">
                      <h5>{smallChalets.bookings.length}</h5>
                      <small>Total Booking Records</small>
                    </div>
                  </div>

                  {smallChalets.bookings.length > 0 && (
                    <div className="table-responsive">
                      <table className="table table-sm table-striped">
                        <thead className="table-dark">
                          <tr>
                            <th>ID</th>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Chalet ID</th>
                            <th>Arrival</th>
                            <th>Departure</th>
                            <th>Nights</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {smallChalets.bookings.map((b) => (
                            <tr key={b.id} className={(b.chaletId || '').includes("NULL") ? "table-danger" : ""}>
                              <td>{b.id}</td>
                              <td><code>{b.orderNumber}</code></td>
                              <td>{b.customer}</td>
                              <td>
                                {(b.chaletId || '').includes("NULL") ? (
                                  <span className="badge bg-danger">⚠️ {b.chaletId}</span>
                                ) : (
                                  <span className="badge bg-secondary">#{b.chaletId}</span>
                                )}
                              </td>
                              <td>{b.arrival}</td>
                              <td>{b.departure}</td>
                              <td>{b.nights}</td>
                              <td>
                                <span className={`badge ${b.isBlocked ? "bg-dark" : "bg-success"}`}>
                                  {b.isBlocked ? "Blocked" : b.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* NULL Chalet Bookings - THE PROBLEM */}
              {nullChaletBookings.length > 0 && (
                <div className="card mb-4 border-danger">
                  <div className="card-header bg-danger text-white">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    🚨 PROBLEMATIC BOOKINGS (No Chalet Assigned) - {nullChaletBookings.length} records
                  </div>
                  <div className="card-body">
                    <div className="alert alert-warning mb-3">
                      <strong>These bookings MUST be fixed!</strong> Each one needs a ChaletId assigned.
                      You can fix this by:
                      <ul className="mb-0 mt-2">
                        <li>Editing the booking directly in the database</li>
                        <li>Using a SQL UPDATE statement to assign ChaletId</li>
                        <li>Creating an admin endpoint to fix these bookings</li>
                      </ul>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-danger">
                        <thead className="table-dark">
                          <tr>
                            <th>Booking ID</th>
                            <th>Order #</th>
                            <th>Customer</th>
                            <th>Chalet Type</th>
                            <th>Arrival</th>
                            <th>Departure</th>
                            <th>Status</th>
                            <th>Warning</th>
                          </tr>
                        </thead>
                        <tbody>
                          {nullChaletBookings.map((b) => (
                            <tr key={b.id}>
                              <td><strong>{b.id}</strong></td>
                              <td><code>{b.orderNumber}</code></td>
                              <td>{b.customer}</td>
                              <td>{b.chaletType}</td>
                              <td>{b.arrival}</td>
                              <td>{b.departure}</td>
                              <td>
                                <span className={`badge ${b.isBlocked ? "bg-dark" : "bg-warning"}`}>
                                  {b.isBlocked ? "Blocked" : b.status}
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-danger">{b.WARNING || 'No chalet assigned'}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Raw JSON Toggle */}
              <div className="card mb-4">
                <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                  <span>
                    <i className="fas fa-code me-2"></i>
                    Raw JSON Response
                  </span>
                  <div>
                    <button
                      className="btn btn-sm btn-light me-2"
                      onClick={() => setShowRawJson(!showRawJson)}
                    >
                      {showRawJson ? "Hide" : "Show"}
                    </button>
                    <button className="btn btn-sm btn-outline-light" onClick={copyToClipboard}>
                      <i className="fas fa-copy me-1"></i>
                      Copy
                    </button>
                  </div>
                </div>
                {showRawJson && (
                  <div className="card-body">
                    <pre className="bg-dark text-light p-3 rounded" style={{ maxHeight: "400px", overflow: "auto" }}>
                      {rawJson}
                    </pre>
                  </div>
                )}
              </div>

              {/* SQL Fix Suggestions */}
              {nullChaletBookings.length > 0 && (
                <div className="card border-warning">
                  <div className="card-header bg-warning text-dark">
                    <i className="fas fa-database me-2"></i>
                    Suggested SQL Fixes
                  </div>
                  <div className="card-body">
                    <p>Run these SQL statements to fix the NULL chalet bookings (adjust ChaletId as needed):</p>
                    <pre className="bg-dark text-light p-3 rounded">
{nullChaletBookings.map((b) => 
`-- Fix booking ${b.id} (${b.customer}, ${b.arrival} - ${b.departure})
-- For ${b.chaletType || 'Unknown'} type: assign an available chalet
UPDATE Bookings SET ChaletId = [AVAILABLE_CHALET_ID] WHERE Id = ${b.id};
`).join('\n')}
                    </pre>
                    <div className="alert alert-info mt-3">
                      <strong>Note:</strong> Before running these, check which chalets are actually available 
                      for each date range to avoid double-booking.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Initial State */}
          {!data && !loading && !error && (
            <div className="text-center text-muted py-5">
              <i className="fas fa-bug fa-3x mb-3"></i>
              <p>Select a date range and click "Run Debug Query" to see raw availability data.</p>
              <p className="small">This tool shows exactly what the backend calculates, including problematic bookings.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DebugAvailability;
